import React, { useEffect, useState, useRef } from "react";
import {
  StyledLink,
  DownloadText
} from "pages/PanelPage/components/selectedAssessment/assessmentResults/takerResults/questionsTable/Question/styles";
import { ToggleStyledButton } from "components/Atoms/Buttons";
import { Container, FlexContainer } from "components/Atoms/Containers";
import downloadIcon from "assets/downloadIcon";

const SPEED_OPTIONS = [1, 1.5, 2, 2.5];

/**
 * VideoResponse component renders a list of video files and provides download links for each video.
 */
const VideoResponse = ({ answer, attemptId }) => {
  const [downloadUrls, setDownloadUrls] = useState({});
  const fileName =
    answer && `QUESTION_${answer.question_upload_id}_ATTEMPT_${attemptId}.webm`;
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const videoRefs = useRef({});

  useEffect(() => {
    if (!answer?.files || answer.files.length === 0) return;

    const objectUrls = {};

    const fetchAll = answer.files.map(async file => {
      try {
        const response = await fetch(file.gcs_link);
        if (!response.ok) throw new Error("Network response was not ok");
        const blob = await response.blob();
        objectUrls[file.id] = URL.createObjectURL(blob);
      } catch (error) {
        console.error("Failed to fetch video:", error);
      }
    });

    Promise.all(fetchAll).then(() => {
      setDownloadUrls({ ...objectUrls });
    });

    return () => {
      Object.values(objectUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [answer]);

  useEffect(() => {
    Object.values(videoRefs.current).forEach(videoEl => {
      if (videoEl) videoEl.playbackRate = playbackSpeed;
    });
  }, [playbackSpeed]);

  return (
    <FlexContainer
      direction={"column"}
      gap={"16"}
      style={{ minHeight: "13em", marginBottom: "20px" }}
      justify={"space-around"}
    >
      {answer?.files && answer.files.length > 0 ? (
        answer.files.map(file => {
          return (
            <FlexContainer key={file.id} direction={"column"} gap={"16"}>
              <video
                controls
                width="480"
                role="video"
                preload="metadata"
                src={file.gcs_link}
                ref={el => {
                  videoRefs.current[file.id] = el;
                }}
                onLoadedMetadata={e => {
                  const video = e.currentTarget;
                  if (video.duration !== Infinity) return;
                  // WebM files from MediaRecorder lack a duration header; seek to
                  // end to force Chrome to scan the file and infer total length,
                  // then reset so playback starts from the beginning.
                  const restoreTime = () => {
                    if (video.duration !== Infinity) {
                      video.currentTime = 0;
                      video.removeEventListener("timeupdate", restoreTime);
                    }
                  };
                  video.addEventListener("timeupdate", restoreTime);
                  video.currentTime = 1e101;
                }}
              >
                <track default kind="captions" srcLang="en" />
                There is an issue with the recorded video.
              </video>
              <FlexContainer
                direction={"row"}
                gap={"16"}
                width={"480px"}
                justify={"space-between"}
              >
                <span style={{ alignSelf: "center" }}>Playback speed</span>
                <FlexContainer
                  direction={"row"}
                  gap={"8"}
                  justify={"flex-end"}
                >
                  {SPEED_OPTIONS.map(speed => (
                    <ToggleStyledButton
                      key={speed}
                      onClick={() => setPlaybackSpeed(speed)}
                      selected={playbackSpeed === speed}
                    >
                      {speed}x
                    </ToggleStyledButton>
                  ))}
                </FlexContainer>
              </FlexContainer>
              <StyledLink
                href={downloadUrls[file.id] || file.gcs_link}
                download={fileName}
                mediaType="video/webm"
              >
                <Container margin="-2px 0 0">{downloadIcon}</Container>
                <DownloadText>Download video ({fileName})</DownloadText>
              </StyledLink>
            </FlexContainer>
          );
        })
      ) : (
        <p>Invalid video link or video not available.</p>
      )}
    </FlexContainer>
  );
};

export { VideoResponse };
